import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './useAuth';

/**
 * Standalone-hub kiosk auto-login: ingest a ready-minted `?deviceToken=`.
 *
 * Spec: specs/20260617-resident-app-kiosk/spec.md  AC3, AC4, AC5
 * Plan: specs/20260617-resident-app-kiosk/plan.md  §3
 *
 * On a standalone hub the touchscreen boots into Chromium kiosk pointed at
 * `https://app.caro.care/?deviceToken=<jwt>`. The hub already minted that
 * DeviceToken server-side via the authenticated MAC endpoint
 * (`POST /api/v1/auth/device-token-by-mac`), so — unlike the Hexnode UDID flow —
 * the app does NOT call any provisioning API. It only needs to INGEST the token:
 * read `?deviceToken=` on first load, store it in the SAME `caro_auth` store the
 * UDID / qrActivate / admin-setup flows use (via `useAuth.setAccessToken`), then
 * strip the param from the URL so the token never lingers in history.
 *
 * Mirrors the param-handling style of `useAutoProvision.ts` (read URL → act →
 * strip via history.replaceState). It tolerates `?hubMac=` as a no-op signal.
 * Fail-closed: no token (and no existing session) → route to `/admin-setup`
 * (the manual PIN fallback), exactly like the UDID path — never a wrong-unit
 * login. A malformed/empty token value is treated as "no token".
 */

export interface DeviceTokenParamDeps {
  /** True when a DeviceToken / access token is already stored. */
  hasToken: () => boolean;
  /** Raw URL query string, e.g. `window.location.search`. */
  getUrlSearch: () => string;
  /** Persist the minted device token (same store as qrActivate / admin flow). */
  storeToken: (token: string) => void;
  /**
   * Persist the user profile the hub passed alongside the token. REQUIRED for
   * the app to become authenticated: the shared auth store flips
   * `isAuthenticated` true via `setUser` (not `setAccessToken`), and a populated
   * user suppresses the User-JWT `/api/auth/me` hydration that a DeviceToken
   * cannot satisfy (it's a user-JWT endpoint → 401). Mirrors useAutoProvision.
   */
  storeUser?: (user: unknown) => void;
  /** Strip the `deviceToken` / `u` / `hubMac` params via history.replaceState. */
  stripUrl: () => void;
  /** Router navigate. */
  navigate: (path: string) => void;
}

/**
 * Decode the `u` param — a base64url-encoded JSON user profile minted by the hub
 * (`device-token-by-mac` response `.data.user`). base64url so it survives the
 * query string (no +,/,= that URLSearchParams would mangle). UTF-8 safe.
 */
export function decodeUserParam(u: string): unknown | null {
  try {
    let b64 = u.replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4 !== 0) b64 += '=';
    const bin = atob(b64);
    const json = decodeURIComponent(
      Array.from(bin).map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0')).join(''),
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export interface DeviceTokenParamOutcome {
  /** True when the app ends up authenticated (ingested now OR already was). */
  authenticated: boolean;
  /** True when a token was ingested from the URL on this run. */
  ingested?: boolean;
}

/**
 * Pure, dependency-injected core of the kiosk token-ingest flow. Exported so it
 * can be unit-tested without mounting React (mirrors `runAutoProvision`).
 */
export async function runDeviceTokenParam(
  deps: DeviceTokenParamDeps,
): Promise<DeviceTokenParamOutcome> {
  const hadToken = deps.hasToken();

  // 1. Read the deviceToken from the URL.
  let deviceToken: string | null = null;
  try {
    const params = new URLSearchParams(deps.getUrlSearch() || '');
    const fromUrl = params.get('deviceToken');
    if (fromUrl && fromUrl.trim()) {
      deviceToken = fromUrl.trim();
    }
  } catch {
    // URL parsing failed — treat as no token.
  }

  // 2. Valid token present → ingest token + user, strip the params, authenticate.
  if (deviceToken) {
    deps.storeToken(deviceToken);
    // Ingest the user profile the hub passed (base64url JSON in `u`). Without
    // setUser the app is NOT authenticated and would call /api/auth/me with the
    // device token → 401. (Mirrors useAutoProvision's success path.)
    try {
      const params = new URLSearchParams(deps.getUrlSearch() || '');
      const u = params.get('u');
      if (u && deps.storeUser) {
        const user = decodeUserParam(u);
        if (user) deps.storeUser(user);
      }
    } catch {
      // malformed user param — token is still stored; app may fall back to /me.
    }
    // Strip on success so token/user are not retained in history.
    try { deps.stripUrl(); } catch { /* history API unavailable — leave URL */ }
    return { authenticated: true, ingested: true };
  }

  // 3. No deviceToken in the URL.
  if (hadToken) {
    return { authenticated: true };
  }
  // CRITICAL: do NOT hijack the Hexnode `?hexnodeUDID=` flow (production-critical,
  // handled by useAutoProvision) or the normal login. Only take over when this is
  // genuinely a kiosk fallback — i.e. the hub launcher explicitly passed
  // `?provisionError=` AND there is no `?hexnodeUDID=` to provision. Otherwise
  // this hook is a complete no-op and defers to useAutoProvision / the router.
  let provisionError: string | null = null;
  let hubMac: string | null = null;
  let hasHexnodeUdid = false;
  try {
    const src = new URLSearchParams(deps.getUrlSearch() || '');
    provisionError = src.get('provisionError');
    hubMac = src.get('hubMac');
    hasHexnodeUdid = Boolean(src.get('hexnodeUDID'));
  } catch {
    // URL parsing failed — defer to other flows.
  }

  if (provisionError && !hasHexnodeUdid) {
    const out = new URLSearchParams();
    out.set('provisionError', provisionError);
    if (hubMac) out.set('hubMac', hubMac);
    deps.navigate(`/admin-setup?${out.toString()}`);
    return { authenticated: false };
  }

  // Not a deviceToken and not a kiosk fallback → no-op; leave the Hexnode UDID
  // flow and the normal login path entirely untouched.
  return { authenticated: false };
}

// MODULE-LEVEL guard: ingest must run at most ONCE per page load, surviving a
// transient AutoProvisionBoot-style remount (mirrors useAutoProvision's guard).
let deviceTokenIngestAttempted = false;

/**
 * React hook: ingests `?deviceToken=` once on cold start. Wire it at the app
 * root alongside `useAutoProvision`. Both are idempotent param-strippers; this
 * one wins when a deviceToken is present (the hub minted it server-side, so it
 * needs no provisioning round-trip).
 */
export function useDeviceTokenParam(): void {
  const { accessToken, setAccessToken, setUser } = useAuth();
  const navigate = useNavigate();
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current || deviceTokenIngestAttempted) return;
    ranRef.current = true;
    deviceTokenIngestAttempted = true;

    void runDeviceTokenParam({
      hasToken: () => Boolean(accessToken),
      getUrlSearch: () => (typeof window !== 'undefined' ? window.location.search : ''),
      storeToken: (token) => {
        // The resident app uses the device JWT as its access token for
        // Authorization: Bearer (same store qrActivate / admin-setup write to,
        // persisted to the `caro_auth` localStorage store by useAuth).
        setAccessToken(token);
      },
      storeUser: (user) => {
        // setUser flips isAuthenticated → true and suppresses the User-JWT
        // /api/auth/me hydration that a DeviceToken can't satisfy. `unknown` →
        // User cast mirrors useAutoProvision (hub-minted resident profile shape).
        setUser(user as any);
      },
      stripUrl: () => {
        try {
          const params = new URLSearchParams(window.location.search);
          params.delete('deviceToken');
          params.delete('u');
          params.delete('hubMac');
          const qs = params.toString();
          const cleanUrl = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
          window.history.replaceState({}, '', cleanUrl);
        } catch {
          // history API unavailable — leave the URL as-is.
        }
      },
      navigate: (path) => navigate(path),
    });
  }, [accessToken, setAccessToken, setUser, navigate]);
}
